import { useState } from 'react';
import { useStore } from '../store';
import { exportApi } from '../services/api';
import { X, FileText, File, Code, Loader2, Download } from 'lucide-react';
import clsx from 'clsx';

interface Props {
  onClose: () => void;
}

type ExportFormat = 'pdf' | 'docx' | 'html';

export default function ExportModal({ onClose }: Props) {
  const { sections, applicantData, currentSessionName, genericSections, addNotification } = useStore();
  const [format, setFormat] = useState<ExportFormat>('pdf');
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    if (sections.length === 0) {
      addNotification('No content to export', 'error');
      return;
    }

    setExporting(true);
    try {
      // Build applicant data with names from generic sections
      const exportApplicantData = { ...applicantData };
      genericSections.forEach((section) => {
        section.fields.forEach((field) => {
          if (field.id === 'primary_name' && field.value) {
            exportApplicantData.beneficiaryName = field.value;
          }
          if (field.id === 'secondary_name' && field.value) {
            exportApplicantData.petitionerName = field.value;
          }
          if (field.id === 'reference_number' && field.value) {
            exportApplicantData.caseNumber = field.value;
          }
        });
      });

      const blob = await exportApi.export({
        format,
        applicantData: exportApplicantData,
        sections: sections.map((s) => ({ title: s.title, content: s.content })),
        documentTitle: currentSessionName,
      });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${currentSessionName.replace(/\s+/g, '_')}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      addNotification('Export complete', 'success');
      onClose();
    } catch (err: any) {
      addNotification(err.response?.data?.error || 'Export failed', 'error');
    } finally {
      setExporting(false);
    }
  };

  const formats: { id: ExportFormat; label: string; icon: typeof FileText; description: string }[] = [
    { id: 'pdf', label: 'PDF', icon: FileText, description: 'Best for printing and sharing' },
    { id: 'docx', label: 'Word', icon: File, description: 'Editable Microsoft Word document' },
    { id: 'html', label: 'HTML', icon: Code, description: 'Web-ready format' },
  ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content max-w-md" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold">Export Document</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600">
            Export "{currentSessionName}" with {sections.length} section(s).
          </p>

          {/* Format selection */}
          <div className="space-y-2">
            {formats.map((f) => (
              <button
                key={f.id}
                onClick={() => setFormat(f.id)}
                className={clsx(
                  'w-full flex items-center gap-3 p-3 rounded-lg border transition-colors text-left',
                  format === f.id
                    ? 'border-accent bg-accent/5'
                    : 'border-gray-200 hover:border-gray-300'
                )}
              >
                <div
                  className={clsx(
                    'p-2 rounded-lg',
                    format === f.id ? 'bg-accent text-white' : 'bg-gray-100 text-gray-500'
                  )}
                >
                  <f.icon size={20} />
                </div>
                <div>
                  <p className="font-medium">{f.label}</p>
                  <p className="text-xs text-gray-500">{f.description}</p>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t border-gray-200">
          <button onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button
            onClick={handleExport}
            disabled={exporting || sections.length === 0}
            className="btn btn-primary flex items-center gap-2"
          >
            {exporting ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download size={16} />
                Export
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
